import React from 'react';
import {
  numberToCurrencyString,
  numberToPercentageString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { AllocationRule } from 'further-types/lp/allocations';
import { Controller, useFormContext } from 'react-hook-form';
import { EqualisationGeneralCapitalCallsFormValues } from './FormWrapper';
import useEqualiseGeneralCapital from '../../hooks/useEqualiseGeneralCapital';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';

type GeneralCapitalCallAllocationsFormProps = {
  fields: EqualisationGeneralCapitalCallsFormValues['generalCapitalCalls'][number]['newInvestorBreakdown'];
  totalAmountLcy: number;
  allocationRule: AllocationRule;
  actionIndex: number;
  baseCurrency: CurrencyCode;
  originalInvestorBreakdown: Array<{
    commitmentId: string;
    calledFundsLcy: number;
  }>;
};

const GeneralCapitalCallAllocationsForm: React.FC<
  GeneralCapitalCallAllocationsFormProps
> = ({
  fields,
  allocationRule,
  totalAmountLcy,
  actionIndex,
  baseCurrency,
  originalInvestorBreakdown,
}) => {
  const { control } =
    useFormContext<EqualisationGeneralCapitalCallsFormValues>();

  const {
    watchedFields,
    totalCalledFunds,
    isPending,
    totalAllocationPercentage,
    excludedInvestorNames,
    newInvestorBreakdownPath,
    onChangeAllocationRule,
    handleCalledFundsChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
  } = useEqualiseGeneralCapital({
    actionIndex,
    totalAmountLcy,
    originalInvestorBreakdown,
  });

  const rows = fields.map((field) => ({
    ...field,
    id: field.commitmentId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investorName',
      label: 'Investor',
      width: 200,
      render: (row) => row.investorName,
      footer: `${watchedFields?.length || 0} investors`,
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment',
      width: 180,
      render: (row) =>
        numberToCurrencyString(row.totalCommitmentLcy, {
          currency: baseCurrency,
        }),
    },
    {
      key: 'calledFundsLcy',
      label: `Called funds (${baseCurrency})`,
      width: 180,
      render: (row, investorIndex) => (
        <Controller
          control={control}
          name={`${newInvestorBreakdownPath}.${investorIndex}.calledFundsLcy`}
          render={({ field }) => (
            <NumberInput
              value={field.value}
              currency={baseCurrency}
              currencyStartAdornment
              decimals={2}
              fixedDecimalScale
              disabled={row.excludeFromAllocation || isPending}
              onChange={(value) =>
                handleCalledFundsChange(value ?? 0, investorIndex)
              }
            />
          )}
        />
      ),
      footer: totalCalledFunds.toLocaleString('en-US', {
        style: 'currency',
        currency: baseCurrency,
      }),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation % of called funds',
      width: 200,
      render: (row, investorIndex) => (
        <Controller
          control={control}
          name={`${newInvestorBreakdownPath}.${investorIndex}.allocationPercentage`}
          render={({ field }) => (
            <NumberInput
              value={field.value}
              decimals={5}
              disabled={row.excludeFromAllocation || isPending}
              fixedDecimalScale
              showPercentSuffix
              onChange={(value) =>
                handleAllocationPercentageChange(value ?? 0, investorIndex)
              }
            />
          )}
        />
      ),
      footer: numberToPercentageString(totalAllocationPercentage, {
        accuracy: 5,
        noMultiply: true,
        forceDecimals: true,
      }),
    },
    {
      key: 'callOverpaidOrUnderpaid',
      label: 'Call underpaid/(overpaid)',
      width: 180,
      render: (row) =>
        numberToCurrencyString(row.callOverpaidOrUnderpaid, {
          currency: baseCurrency,
          negativeInBrackets: true,
        }),
    },
  ];

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      checkboxes={{
        isCheckedRow: (row) => !row.excludeFromAllocation,
        onRowCheckedToggle: (row, rowIndex) => {
          handleExcludeFromAllocationChange(
            !row.excludeFromAllocation,
            rowIndex,
          );
        },
      }}
      allocationRule={allocationRule}
      allocationRuleOptions={[
        {
          value: AllocationRule.ByCommitmentAndClosingDate,
          label: `By commitment and closing date${
            excludedInvestorNames ? ` excluding ${excludedInvestorNames}` : ''
          }`,
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
      ]}
      onChangeAllocationRule={onChangeAllocationRule}
    />
  );
};

export default GeneralCapitalCallAllocationsForm;
